/* ============================================================
   Lumavay quiz funnel runtime.

   Behaviours replicated from the reference funnel's own runtime
   (Clarflow's funnel-runtime.Aejw18vg.js) rather than guessed at:

   - step position = BFS depth from start, ifElse/exit transparent;
     progress = (position - 1) / (totalSteps - 1)
   - single-select auto-advances 500ms after the tap, no button rendered
   - multi-select / no-question steps get the sticky pill, disabled
     until every question on the step has an answer
   - {{var}} interpolation via /\{\{([a-zA-Z][a-zA-Z0-9_]*)\}\}/g,
     multi answers joined with ", ", applied to custom HTML too
   - step loader: 100ms tick, percent shown is the CURRENT step's,
     then 300ms and auto-advance

   Deliberate departures from it are marked where they happen: the
   multi-step loader's per-bar curves and the native date field.
   ============================================================ */

(function () {
  'use strict';

  var F = window.FUNNEL;
  var NODES = F.nodes;
  var EDGES = F.edges;
  var POS = F.stepPositions;
  var TOTAL = F.totalSteps;

  // ------------------------------------------------------------- assets
  // Standalone dev serves images from ./assets/. On Shopify, theme assets are a
  // flat, versioned namespace, so the URLs can't be derived by concatenation —
  // sections/quiz-funnel.liquid publishes a filename -> CDN URL map instead.
  var ASSETS = window.QUIZ_ASSETS || null;

  function assetUrl(url) {
    if (!url || !ASSETS) return url;
    return ASSETS[url.replace(/^assets\//, '')] || url;
  }

  // ---------------------------------------------------------------- state

  var state = {
    // stepId -> { questionIndex -> [optionId, ...] } and { inputIndex -> value }
    answers: {},
    inputs: {},
    vars: {},          // variableName -> string | string[]
    path: [],          // visited step ids, in order
    navDir: 'fwd'
  };

  var els = {};
  var pendingAdvance = null;
  var activeTimers = [];

  function clearTimers() {
    activeTimers.forEach(function (t) { clearInterval(t); clearTimeout(t); });
    activeTimers = [];
    if (pendingAdvance) { clearTimeout(pendingAdvance); pendingAdvance = null; }
  }

  // ------------------------------------------------------- interpolation

  var VAR_RE = /\{\{([a-zA-Z][a-zA-Z0-9_]*)\}\}/g;

  function displayValue(name) {
    var v = state.vars[name];
    if (v == null) return undefined;
    if (Array.isArray(v)) return v.length ? v.join(', ') : undefined;
    return v === '' ? undefined : v;
  }

  function interpolate(text) {
    if (!text || text.indexOf('{{') === -1) return text;
    return text.replace(VAR_RE, function (whole, name) {
      var v = displayValue(name);
      return v === undefined ? whole : v;
    });
  }

  // --------------------------------------------------------------- graph

  function edgesFrom(id) {
    return EDGES.filter(function (e) { return e.from === id; });
  }

  function selectedIdsFor(stepId, qIndex) {
    var a = state.answers[stepId];
    return (a && a[qIndex]) || [];
  }

  function questionElements(node) {
    return (node.subEls || []).filter(function (s) { return s.kind === 'question'; });
  }

  /* criteria: { stepId, questionIndex, optionIds } — true when the answer to
     that question includes ANY of the listed option ids. */
  function conditionMatches(cond) {
    if (!cond.criteria || !cond.criteria.length) return true;
    return cond.criteria.every(function (c) {
      var picked = selectedIdsFor(c.stepId, c.questionIndex);
      return (c.optionIds || []).some(function (id) { return picked.indexOf(id) !== -1; });
    });
  }

  /* Walk forward from a node, resolving ifElse nodes, until the next real
     step (or the exit). `optionIndex` routes per-option conditional steps. */
  function resolveNext(fromId, optionIndex) {
    var out = edgesFrom(fromId);
    if (!out.length) return null;

    var node = NODES[fromId];
    var next = null;

    if (node && node.kind === 'ifElse') {
      var ifCond = (node.conditions || []).filter(function (c) { return c.type === 'if'; })[0];
      var elseCond = (node.conditions || []).filter(function (c) { return c.type === 'else'; })[0];
      var handle = (ifCond && conditionMatches(ifCond)) ? ifCond.handleId
                 : (elseCond ? elseCond.handleId : null);
      // the export carries the handle on the edge as `sourceConditionId`
      next = out.filter(function (e) { return e.sourceConditionId === handle; })[0];
      if (!next) next = handle === 'condition-if' ? out[0] : out[out.length - 1];
    } else if (node && node.isConditional && typeof optionIndex === 'number') {
      next = out.filter(function (e) { return e.sourceOptionIndex === optionIndex; })[0] || out[0];
    } else {
      next = out.filter(function (e) { return e.sourceOptionIndex === undefined; })[0] || out[0];
    }

    if (!next) return null;
    var target = NODES[next.to];
    if (target && target.kind === 'ifElse') return resolveNext(next.to);
    return next.to;
  }

  // ---------------------------------------------------------------- chrome

  function updateChrome() {
    var stepId = state.path[state.path.length - 1];
    var pos = POS[stepId] || 1;
    var pct = TOTAL > 1 ? ((pos - 1) / (TOTAL - 1)) * 100 : 100;
    els.progressFill.style.width = Math.max(pct, 1.5) + '%';

    var isFirst = state.path.length <= 1;
    els.promo.classList.toggle('is-visible', isFirst);
    els.back.classList.toggle('is-visible', !isFirst);
  }

  // ---------------------------------------------------------------- render

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  function slug(name) {
    return String(name || '').toLowerCase()
      .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function renderTitle(sub) {
    return el('div', 'cf-el cf-el-title cf-title', interpolate(sub.text));
  }

  function renderText(sub) {
    var wrap = el('div', 'cf-el cf-el-text cf-text');
    if (sub.isRichText || sub.contentFormat === 'html') {
      wrap.innerHTML = interpolate(sub.text);
    } else {
      wrap.appendChild(el('p', null, interpolate(sub.text)));
    }
    return wrap;
  }

  function renderImage(sub) {
    var wrap = el('div', 'cf-el cf-el-image');
    var img = document.createElement('img');
    img.className = 'cf-image';
    img.src = assetUrl(sub.url);
    img.alt = sub.alt || '';
    wrap.appendChild(img);
    return wrap;
  }

  function renderCustomHtml(sub) {
    var wrap = el('div', 'cf-el cf-custom-html');
    var frame = document.createElement('iframe');
    frame.className = 'cf-html-frame';
    frame.setAttribute('scrolling', 'no');
    frame.setAttribute('title', 'embedded content');
    frame.srcdoc = interpolate(sub.html);
    wrap.appendChild(frame);

    function fit() {
      try {
        var doc = frame.contentDocument;
        if (!doc || !doc.body) return;
        // measure the BODY, not documentElement — the latter stretches to the
        // iframe's own height and the reading would only ever grow
        frame.style.height = doc.body.scrollHeight + 'px';
      } catch (e) { /* ignore */ }
    }
    frame.addEventListener('load', function () {
      fit();
      // re-fit once fonts/images inside settle
      setTimeout(fit, 60);
      setTimeout(fit, 300);
      try {
        if (window.ResizeObserver && frame.contentDocument) {
          new ResizeObserver(fit).observe(frame.contentDocument.body);
        }
      } catch (e) { /* ignore */ }
    });
    return wrap;
  }

  var CHECK_SVG =
    '<svg width="14" height="14" viewBox="0 0 12 12" fill="none">' +
    '<path d="M2.5 6.2l2.3 2.3 4.7-5" stroke="#fff" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function renderQuestion(stepId, sub, qIndex, node, onChange) {
    var isGrid = sub.layout === 'grid';
    var isMulti = sub.kindOf === 'multi';

    var wrap = el('div', 'cf-el cf-el-question ' +
      (isMulti ? 'cf-question-multi' : 'cf-question-single'));
    var list = el('div', isGrid ? 'cf-options-grid' : 'cf-options');
    wrap.appendChild(list);

    (sub.options || []).forEach(function (opt, i) {
      var btn = el('button', 'cf-option');
      btn.type = 'button';

      if (isGrid) {
        if (opt.imageUrl) {
          var img = document.createElement('img');
          img.className = 'cf-option-img';
          img.src = assetUrl(opt.imageUrl);
          img.alt = opt.text;
          btn.appendChild(img);
        }
        var cap = el('div', 'cf-option-cap');
        cap.appendChild(el('span', 'cf-option-cap-label', interpolate(opt.text)));
        if (opt.subtext) cap.appendChild(el('span', 'cf-option-cap-sub', opt.subtext));
        btn.appendChild(cap);
      } else {
        if (opt.emoji) btn.appendChild(el('span', 'cf-option-emoji', opt.emoji));
        btn.appendChild(el('span', 'cf-option-label', interpolate(opt.text)));
      }

      btn.addEventListener('click', function () {
        var picked = selectedIdsFor(stepId, qIndex).slice();
        if (isMulti) {
          var at = picked.indexOf(opt.id);
          if (at === -1) picked.push(opt.id); else picked.splice(at, 1);
        } else {
          picked = [opt.id];
        }

        state.answers[stepId] = state.answers[stepId] || {};
        state.answers[stepId][qIndex] = picked;

        if (sub.variableName) {
          var labels = picked.map(function (id) {
            var o = (sub.options || []).filter(function (x) { return x.id === id; })[0];
            return o ? o.text : id;
          });
          state.vars[sub.variableName] = isMulti ? labels : (labels[0] || '');
        }

        // repaint selection
        Array.prototype.forEach.call(list.children, function (child, ci) {
          var id = (sub.options[ci] || {}).id;
          child.classList.toggle('is-selected', picked.indexOf(id) !== -1);
        });

        onChange();

        // single-select auto-advance, 500ms, exactly like the source runtime
        if (!isMulti && picked.length) {
          if (pendingAdvance) clearTimeout(pendingAdvance);
          pendingAdvance = setTimeout(function () {
            pendingAdvance = null;
            var routeIndex = (node.isConditional && node.conditionalQuestionIndex === qIndex) ? i : undefined;
            goNext(routeIndex);
          }, 500);
          activeTimers.push(pendingAdvance);
        }
      });

      if (selectedIdsFor(stepId, qIndex).indexOf(opt.id) !== -1) {
        btn.classList.add('is-selected');
      }
      list.appendChild(btn);
    });

    return wrap;
  }

  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];

  /* "2026-09-14" -> "September 14, 2026". Parsed by hand: `new Date(iso)` reads
     a bare date as UTC and shifts a day backwards west of Greenwich. */
  function prettyDate(iso) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
    if (!m) return iso || '';
    return MONTHS[+m[2] - 1] + ' ' + (+m[3]) + ', ' + m[1];
  }

  function todayIso() {
    var d = new Date();
    return d.getFullYear() + '-' +
      String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0');
  }

  function renderInput(stepId, sub, index, onChange) {
    var wrap = el('div', 'cf-el cf-el-input cf-input');
    if (sub.label) wrap.appendChild(el('label', 'cf-input-label', interpolate(sub.label)));

    var isDate = sub.inputType === 'date';
    var input = document.createElement('input');
    input.type = sub.inputType || 'text';
    input.placeholder = sub.placeholder || '';
    input.value = (state.inputs[stepId] && state.inputs[stepId][index]) || '';

    if (isDate) {
      // The event is ahead of the user, so the past is not selectable and the
      // calendar opens on this month. Native picker on purpose: it is the wheel
      // on iOS/Android and the calendar dropdown on desktop, with no library.
      input.className = 'cf-date-input';
      input.min = todayIso();
      var host = el('div', 'cf-date');
      host.appendChild(input);
      host.appendChild(el('span', 'cf-date-icon',
        '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
        'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
        '<rect x="3" y="4.5" width="18" height="16" rx="2"></rect>' +
        '<line x1="3" y1="9.5" x2="21" y2="9.5"></line>' +
        '<line x1="8" y1="2.5" x2="8" y2="6.5"></line>' +
        '<line x1="16" y1="2.5" x2="16" y2="6.5"></line></svg>'));
      // Tapping anywhere on the field opens the picker: the browser's own
      // indicator is stretched over the whole input (see .cf-date-input in the
      // CSS), so clicks on the input are already handled natively. This only
      // covers the wrapper's own padding, and never fires on the input itself
      // — calling showPicker() while the native picker is opening would toggle
      // it straight back shut.
      host.addEventListener('click', function (e) {
        if (e.target === input) return;
        try { if (input.showPicker) input.showPicker(); } catch (err) { input.focus(); }
      });
      // an empty date field is still :valid, so the greyed-out dd/mm/yyyy state
      // has to be flagged from here rather than in CSS
      function markEmpty() { host.classList.toggle('is-empty', !input.value); }
      markEmpty();
      input.addEventListener('input', markEmpty);
      input.addEventListener('change', markEmpty);
      wrap.appendChild(host);
    } else {
      wrap.appendChild(input);
    }

    input.addEventListener('input', function () {
      state.inputs[stepId] = state.inputs[stepId] || {};
      state.inputs[stepId][index] = input.value;
      if (sub.variableName) {
        state.vars[sub.variableName] = isDate ? prettyDate(input.value) : input.value;
      }
      onChange();
    });
    return wrap;
  }

  // ------------------------------------------------------------ loaders

  function easeInOutCubic(t) {
    return t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  /* Clarflow's per-step curve: first half of the duration eases 0→50,
     second half eases 50→100. Every bar therefore moved in lockstep, which is
     what made the loader read as scripted. Kept for the single transfer bar. */
  function visualPercent(elapsed, durationMs) {
    if (elapsed <= 0) return { pct: 0, done: false };
    var half = durationMs / 2;
    if (elapsed < half) return { pct: easeInOutCubic(elapsed / half) * 50, done: false };
    if (elapsed < durationMs) return { pct: 50 + easeInOutCubic((elapsed - half) / half) * 50, done: false };
    return { pct: 100, done: true };
  }

  /* Real work does not progress smoothly, so each step in the multi-step loader
     gets its own shape instead of the shared one: keyframes of
     [fraction of the step's duration, percent], linearly interpolated. Flat
     runs between two frames are stalls; steep runs are bursts. */
  var LOADER_CURVES = [
    // fast off the line, then sits on 78 while it "thinks", then snaps shut
    [[0, 0], [.14, 31], [.3, 52], [.42, 71], [.5, 78], [.78, 82], [.88, 94], [1, 100]],
    // reluctant crawl, one small stall low down, accelerates at the end
    [[0, 0], [.18, 9], [.34, 13], [.55, 34], [.72, 56], [.86, 79], [1, 100]],
    // stutters: two visible pauses, a jump between them
    [[0, 0], [.1, 24], [.26, 27], [.5, 61], [.62, 64], [.8, 88], [1, 100]],
    // near-steady with a long hesitation just before the finish
    [[0, 0], [.22, 22], [.44, 47], [.62, 66], [.74, 73], [.9, 77], [.96, 89], [1, 100]]
  ];

  function curvePercent(elapsed, durationMs, curveIndex) {
    if (elapsed <= 0) return { pct: 0, done: false };
    if (elapsed >= durationMs) return { pct: 100, done: true };
    var pts = LOADER_CURVES[curveIndex % LOADER_CURVES.length];
    var t = elapsed / durationMs;
    for (var i = 1; i < pts.length; i++) {
      if (t <= pts[i][0]) {
        var span = pts[i][0] - pts[i - 1][0];
        var k = span ? (t - pts[i - 1][0]) / span : 1;
        return { pct: pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * k, done: false };
      }
    }
    return { pct: 100, done: true };
  }

  function renderStepLoader(sub) {
    var wrap = el('div', 'cf-el cf-el-loading');
    var steps = sub.steps || [];
    var rows = steps.map(function () { return el('div', 'cf-loading-step'); });
    rows.forEach(function (r) { wrap.appendChild(r); });

    function paint(i, status, pct) {
      var s = steps[i];
      var row = rows[i];
      var label = interpolate(status === 'completed' ? s.completedText : s.loadingText);
      if (status === 'completed') {
        row.className = 'cf-loading-step cf-loading-step-completed';
        row.innerHTML =
          '<div class="cf-loading-step-head">' +
            '<span class="cf-loading-step-label"></span>' +
            '<div class="cf-loading-step-check">' + CHECK_SVG + '</div>' +
          '</div><div class="cf-loading-step-divider"></div>';
        row.querySelector('.cf-loading-step-label').textContent = label;
      } else if (status === 'loading') {
        row.className = 'cf-loading-step cf-loading-step-loading';
        row.innerHTML =
          '<div class="cf-loading-step-head">' +
            '<span class="cf-loading-step-label"></span>' +
            '<span class="cf-loading-step-percent">' + Math.round(pct) + '%</span>' +
          '</div>' +
          '<div class="cf-loading-step-track"><div class="cf-loading-step-fill" ' +
          'style="width:' + pct + '%"></div></div>';
        row.querySelector('.cf-loading-step-label').textContent = label;
      } else {
        row.className = 'cf-loading-step cf-loading-step-pending';
        row.innerHTML = '<div class="cf-loading-step-dot"></div>' +
          '<span class="cf-loading-step-label"></span>';
        row.querySelector('.cf-loading-step-label').textContent = label;
      }
    }

    steps.forEach(function (_, i) { paint(i, i === 0 ? 'loading' : 'pending', 0); });

    var active = 0, elapsed = 0;
    var tick = setInterval(function () {
      elapsed += 100;
      var cur = steps[active];
      if (!cur) return;
      var r = curvePercent(elapsed, (cur.seconds || 1) * 1000, active);
      if (r.done) {
        paint(active, 'completed', 100);
        active += 1;
        elapsed = 0;
        if (active >= steps.length) {
          clearInterval(tick);
          var t = setTimeout(function () { goNext(); }, 300);
          activeTimers.push(t);
          return;
        }
        paint(active, 'loading', 0);
        return;
      }
      paint(active, 'loading', r.pct);
    }, 100);
    activeTimers.push(tick);

    return wrap;
  }

  function renderBarLoader(sub) {
    var wrap = el('div', 'cf-el cf-el-loading');
    wrap.innerHTML =
      '<div class="cf-loading-bar-wrap"><div class="cf-loading-bar-track">' +
      '<div class="cf-loading-bar-fill" style="width:0%"></div></div></div>' +
      '<div class="cf-loading-bar-text"><span class="cf-label"></span>' +
      '<span class="cf-pct">0%</span></div>';
    wrap.querySelector('.cf-label').textContent = interpolate(sub.text || '');

    var fill = wrap.querySelector('.cf-loading-bar-fill');
    var pctEl = wrap.querySelector('.cf-pct');
    var elapsed = 0;
    var duration = (sub.seconds || 1) * 1000;

    var tick = setInterval(function () {
      elapsed += 100;
      var r = visualPercent(elapsed, duration);
      fill.style.width = r.pct + '%';
      pctEl.textContent = Math.round(r.pct) + '%';
      if (r.done) {
        clearInterval(tick);
        var t = setTimeout(function () { goNext(); }, 300);
        activeTimers.push(t);
      }
    }, 100);
    activeTimers.push(tick);

    return wrap;
  }

  // ------------------------------------------------------------- the step

  function renderStep() {
    clearTimers();

    var stepId = state.path[state.path.length - 1];
    var node = NODES[stepId];

    els.main.innerHTML = '';
    var step = el('div', 'cf-step cf-step-' + slug(node.name) +
      (state.navDir === 'back' ? ' is-back' : ''));

    var hasLoader = (node.subEls || []).some(function (s) { return s.kind === 'loading'; });
    var questions = questionElements(node);
    var singleOnly = questions.length === 1 && questions[0].kindOf === 'single';

    function syncButton() {
      var ok = questions.every(function (q, qi) {
        var idx = (node.subEls || []).indexOf(q);
        return selectedIdsFor(stepId, idx).length > 0;
      });
      var requiredInputs = (node.subEls || []).filter(function (s) {
        return s.kind === 'input' && s.required;
      });
      var inputsOk = requiredInputs.every(function (s) {
        var idx = (node.subEls || []).indexOf(s);
        return ((state.inputs[stepId] || {})[idx] || '').trim().length > 0;
      });
      els.button.disabled = !(ok && inputsOk);
    }

    (node.subEls || []).forEach(function (sub, index) {
      var block = null;
      switch (sub.kind) {
        case 'title':       block = renderTitle(sub); break;
        case 'text':        block = renderText(sub); break;
        case 'image':       block = renderImage(sub); break;
        case 'custom_html': block = renderCustomHtml(sub); break;
        case 'question':    block = renderQuestion(stepId, sub, index, node, syncButton); break;
        case 'input':       block = renderInput(stepId, sub, index, syncButton); break;
        case 'loading':
          block = sub.style === 'steps' ? renderStepLoader(sub) : renderBarLoader(sub);
          break;
      }
      if (block) step.appendChild(block);
    });

    els.main.appendChild(step);

    // The sticky pill is rendered for every step EXCEPT a step whose only
    // question is single-select (those auto-advance) and loader steps.
    var showButton = !hasLoader && !singleOnly;
    els.portal.classList.toggle('is-visible', showButton);
    if (showButton) {
      els.button.textContent = interpolate(node.buttonText || 'Continue');
      syncButton();
      requestAnimationFrame(function () {
        document.documentElement.style.setProperty(
          '--sf-button-space', (els.portal.offsetHeight + 12) + 'px');
      });
    } else {
      document.documentElement.style.setProperty('--sf-button-space', '24px');
    }

    updateChrome();
    window.scrollTo(0, 0);
  }

  // ------------------------------------------------------------ navigation

  function goNext(optionIndex) {
    var stepId = state.path[state.path.length - 1];
    var node = NODES[stepId];

    // a conditional step routes on the answer to conditionalQuestionIndex
    if (optionIndex === undefined && node.isConditional) {
      var qi = node.conditionalQuestionIndex;
      var q = (node.subEls || [])[qi];
      var picked = selectedIdsFor(stepId, qi)[0];
      if (q && picked) {
        optionIndex = (q.options || []).findIndex(function (o) { return o.id === picked; });
        if (optionIndex === -1) optionIndex = undefined;
      }
    }

    var nextId = resolveNext(stepId, optionIndex);
    if (!nextId) return;

    var target = NODES[nextId];
    if (target && target.kind === 'exit') {
      exitTo(target);
      return;
    }

    state.path.push(nextId);
    state.navDir = 'fwd';
    history.pushState({ step: state.path.length }, '', '?step=' + state.path.length);
    renderStep();
  }

  function goBack() {
    if (state.path.length <= 1) return;
    history.back();
  }

  function exitTo(node) {
    // UTMs appended to the offer page. The source tagged these `clarflow`;
    // ours name the funnel instead. `?view=lumaway2` on the redirect URL survives.
    var url = new URL(node.redirectUrl);
    url.searchParams.set('utm_source', 'quiz');
    url.searchParams.set('utm_medium', 'funnel');
    url.searchParams.set('utm_campaign', 'wrinkle-type-quiz');
    url.searchParams.set('utm_content', node.id);

    // Navigate straight off the finished step. Painting anything here only
    // flashes for the few hundred ms the navigation takes, and the debug
    // readout was leaking into real runs — it is now the QUIZ_REDIRECT=false
    // branch only.
    if (window.QUIZ_REDIRECT !== false) {
      window.location.replace(url.href);
      return;
    }

    els.main.innerHTML = '';
    var box = el('div', 'cf-step');
    box.appendChild(el('div', 'cf-el cf-title', 'Redirecting…'));
    var p = el('div', 'cf-el cf-text');
    p.innerHTML = '<p>Exit step → <a href="' + url.href + '">' + url.href + '</a></p>';
    box.appendChild(p);
    els.main.appendChild(box);
    els.portal.classList.remove('is-visible');
  }

  window.addEventListener('popstate', function (e) {
    var want = (e.state && e.state.step) || 1;
    if (want < state.path.length) {
      state.path = state.path.slice(0, Math.max(want, 1));
      state.navDir = 'back';
      renderStep();
    }
  });

  // ----------------------------------------------------------------- boot

  function boot() {
    els.main = document.getElementById('cf-main');
    els.portal = document.getElementById('cf-portal');
    els.button = document.getElementById('cf-button');
    els.progressFill = document.getElementById('cf-progress-fill');
    els.promo = document.getElementById('cf-promo');
    els.back = document.getElementById('cf-back');

    els.button.addEventListener('click', function () {
      if (els.button.disabled) return;
      goNext();
    });
    els.back.addEventListener('click', goBack);

    var first = resolveNext(F.startId);
    state.path = [first];
    history.replaceState({ step: 1 }, '', '?step=1');
    renderStep();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
